import { useMemo, useState, useCallback, useRef } from 'react';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Bar } from '@visx/shape';
import { Line } from '@visx/shape';

// Margins for the chart
const margin = { top: 20, right: 40, bottom: 50, left: 60 };

// Custom tooltip component matching the design
function CustomTooltip({ title, content, modelBreakdown, style }) {
  return (
    <div
      className="rounded-lg border border-border-default bg-bg-card p-spacing-4 shadow-elevation-2"
      style={{ ...style, minWidth: 280, maxWidth: 400 }}
    >
      <h3 className="font-bold text-primary text-dls-body mb-spacing-3">{title}</h3>
      {modelBreakdown ? (
        <div className="space-y-spacing-2">
          {modelBreakdown.map((model, idx) => (
            <div key={idx} className="flex items-start gap-spacing-2">
              <div 
                className="mt-1 size-3 rounded-full flex-shrink-0" 
                style={{ backgroundColor: model.color }}
              />
              <div className="flex-1 text-secondary text-dls-small">
                <div className="flex justify-between items-baseline">
                  <span className="font-normal">{model.name}</span>
                  <span>${model.cost.toFixed(2)}</span>
                </div>
                <div className="text-dls-small opacity-75">
                  {model.tokens.toLocaleString()} tokens
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-secondary text-dls-small leading-relaxed whitespace-pre-line">{content}</p>
      )}
    </div>
  );
}

export default function UsageBarChart({ 
  data, 
  width, 
  height, 
  xAccessor = d => d.date,
  yAccessor = d => d.value,
  colorAccessor = d => d.color,
  labelAccessor = d => d.label,
  descriptionAccessor = d => d.description,
  currencySymbol = '$'
}) {
  const [hoveredData, setHoveredData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  // Calculate bounds
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // Get all days in the dataset for band scale
  const allDays = useMemo(() => {
    if (data.length === 0) return [];
    
    // Get the first date to determine the month
    const firstDate = new Date(xAccessor(data[0]));
    const year = firstDate.getFullYear();
    const month = firstDate.getMonth();
    
    // Generate all days in the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  }, [data, xAccessor]);

  // Create scales
  const xScale = useMemo(
    () => scaleBand({
      domain: allDays,
      range: [16, xMax],
      padding: 0.2, // 20% padding between bars
    }),
    [allDays, xMax]
  );

  const yScale = useMemo(
    () => {
      const values = data.map(yAccessor);
      if (values.length === 0) {
        return scaleLinear({
          domain: [0, 1],
          range: [yMax, 0],
          nice: true,
        });
      }
      
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      
      // Ensure we have a reasonable domain
      const yMaxDomain = Math.max(1, maxValue * 1.2);
      const yMinDomain = Math.min(0, minValue);
      
      return scaleLinear({
        domain: [yMinDomain, yMaxDomain],
        range: [yMax, 0],
        nice: true,
        clamp: true, // This ensures values outside domain are clamped to range
      });
    },
    [data, yAccessor, yMax]
  );

  // Handle mouse events
  const handleMouseOver = useCallback((datum) => {
    if (svgRef.current) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const dayNum = new Date(xAccessor(datum)).getDate();
      const x = xScale(dayNum) + xScale.bandwidth() / 2 + margin.left;
      const y = yScale(yAccessor(datum)) + margin.top - 10; // Position above the bar
      
      setTooltipPosition({
        x: svgRect.left + x,
        y: svgRect.top + y
      });
      setHoveredData(datum);
    }
  }, [xScale, yScale, xAccessor, yAccessor]);

  const handleMouseLeave = useCallback(() => {
    setHoveredData(null);
  }, []);

  return (
    <>
      <div className="relative">
        <svg ref={svgRef} width={width} height={height}>
          <rect width={width} height={height} fill="transparent" />
          <Group left={margin.left} top={margin.top}>
          {/* Grid lines */}
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="0"
            stroke="var(--color-border-default)"
            strokeOpacity={0.05}
            pointerEvents="none"
          />

          {/* X Axis - without labels */}
          <AxisBottom
            top={yMax}
            scale={xScale}
            numTicks={0}
            stroke="var(--color-border-default)"
            tickStroke="transparent"
          />

          {/* Y Axis - without labels */}
          <AxisLeft
            scale={yScale}
            numTicks={0}
            stroke="var(--color-border-default)"
            tickStroke="transparent"
          />

          {/* Axis labels */}
          <text
            x={xMax / 2}
            y={yMax + 45}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="middle"
          >
            Days
          </text>
          
          <text
            x={-margin.left / 2}
            y={yMax / 2}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="middle"
            transform={`rotate(-90, ${-margin.left / 2}, ${yMax / 2})`}
          >
            {currencySymbol}/day
          </text>
          
          {/* Max value labels */}
          {/* Y-axis max value */}
          <text
            x={-8}
            y={0}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="end"
            alignmentBaseline="middle"
          >
            {`${currencySymbol}${yScale.domain()[1].toFixed(2)}`}
          </text>
          
          {/* Y-axis min value (0) */}
          <text
            x={-8}
            y={yMax}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="end"
            alignmentBaseline="middle"
          >
            {`${currencySymbol}0.00`}
          </text>
          
          {/* X-axis start and end dates */}
          <text
            x={xScale(1) + xScale.bandwidth() / 2}
            y={yMax + 20}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="middle"
          >
            1
          </text>
          
          <text
            x={xScale(allDays[allDays.length - 1]) + xScale.bandwidth() / 2}
            y={yMax + 20}
            fill="var(--color-secondary)"
            fontSize="var(--font-size-small)"
            textAnchor="middle"
          >
            {allDays[allDays.length - 1]}
          </text>

          {/* Data bars */}
          {data.map((d, i) => {
            const dayNum = new Date(xAccessor(d)).getDate();
            const barWidth = xScale.bandwidth();
            const barX = xScale(dayNum);
            const barY = yScale(yAccessor(d));
            const barHeight = Math.max(0, yMax - barY);
            const isHovered = hoveredData === d;
            
            return (
              <Bar
                key={`bar-${i}`}
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={colorAccessor(d)}
                style={{
                  cursor: 'pointer',
                  opacity: isHovered ? 1 : 0.8,
                  transition: 'opacity 200ms ease',
                }}
                onMouseOver={() => handleMouseOver(d)}
                onMouseLeave={handleMouseLeave}
              />
            );
          })}

          {/* Guide lines and values on hover */}
          {hoveredData && (
            <>
              {/* Horizontal guide line to Y-axis */}
              <Line
                from={{ x: 0, y: yScale(yAccessor(hoveredData)) }}
                to={{ x: xScale(new Date(xAccessor(hoveredData)).getDate()) + xScale.bandwidth() / 2, y: yScale(yAccessor(hoveredData)) }}
                stroke="var(--color-border-default)"
                strokeWidth={1}
                strokeDasharray="4,4"
                pointerEvents="none"
              />
              
              
              {/* Connection line to tooltip */}
              <Line
                from={{ 
                  x: xScale(new Date(xAccessor(hoveredData)).getDate()) + xScale.bandwidth() / 2, 
                  y: yScale(yAccessor(hoveredData)) 
                }}
                to={{ 
                  x: xScale(new Date(xAccessor(hoveredData)).getDate()) + xScale.bandwidth() / 2 + 10, 
                  y: yScale(yAccessor(hoveredData)) - 10 
                }}
                stroke="var(--color-border-default)"
                strokeWidth={1}
                strokeDasharray="4,4"
                pointerEvents="none"
              />
              
              {/* Y-axis value */}
              <text
                x={-8}
                y={yScale(yAccessor(hoveredData))}
                fill="var(--color-primary)"
                fontSize="var(--font-size-small)"
                textAnchor="end"
                alignmentBaseline="middle"
              >
                {`${currencySymbol}${yAccessor(hoveredData).toFixed(2)}`}
              </text>
              
              {/* X-axis value */}
              <text
                x={xScale(new Date(xAccessor(hoveredData)).getDate()) + xScale.bandwidth() / 2}
                y={yMax + 20}
                fill="var(--color-primary)"
                fontSize="var(--font-size-small)"
                textAnchor="middle"
              >
                {new Date(xAccessor(hoveredData)).getDate()}
              </text>
            </>
          )}
        </Group>
      </svg>
    </div>

    {/* Tooltip - positioned absolutely on the page */}
    {hoveredData && (
      <div
        className="fixed z-50 pointer-events-none"
        style={{
          left: `${tooltipPosition.x + 10}px`,
          top: `${tooltipPosition.y - 10}px`,
          transform: 'translate(0, -100%)'
        }}
      >
        <CustomTooltip
          title={labelAccessor(hoveredData)}
          content={descriptionAccessor(hoveredData)}
          modelBreakdown={hoveredData.modelBreakdown}
        />
      </div>
    )}
  </>
  );
}
